import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { unknownSizesApi, sizeSchemaApi } from '../../services/api';

export default function UnknownSizesDashboard() {
  const [summary, setSummary] = useState(null);
  const [groups, setGroups] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ retailers: [], families: [] });
  const [filters, setFilters] = useState({ retailer_id: '', family: '', raw_size_search: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldown, setDrilldown] = useState({ retailer_id: '', raw_size: '', rows: [] });
  const [reclassifying, setReclassifying] = useState(false);

  const load = useCallback(async (currentFilters = filters) => {
    try {
      setLoading(true);
      setError(null);
      const [s, g, f] = await Promise.all([
        unknownSizesApi.summary(),
        unknownSizesApi.list({ limit: 200, ...currentFilters }),
        unknownSizesApi.filterOptions(),
      ]);
      setSummary(s);
      setGroups(g.groups || []);
      setFilterOptions(f);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(filters); }, []);

  // Re-query when filters change (debounced for raw_size_search)
  useEffect(() => {
    const t = setTimeout(() => { load(filters); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.retailer_id, filters.family, filters.raw_size_search]);

  const handleDrilldown = async (group) => {
    try {
      setError(null);
      const resp = await unknownSizesApi.drilldown(group.retailer_id, group.raw_size);
      setDrilldown({ retailer_id: group.retailer_id, raw_size: group.raw_size, rows: resp.rows || [] });
      setDrilldownOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to load drill-down');
    }
  };

  const handleReclassify = async () => {
    try {
      setReclassifying(true);
      setInfo(null);
      const result = await sizeSchemaApi.reclassify();
      const stats = result.result?.stats || {};
      setInfo(`Reclassified ${stats.rows_processed || 0} rows in ${Math.round(stats.duration_seconds || 0)}s. Refresh to see updated counts.`);
    } catch (e) {
      setError(e.message || 'Reclassification failed');
    } finally {
      setReclassifying(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">Unknown Sizes</Typography>
          <Typography variant="body2" color="text.secondary">
            Sizes the deterministic classifier could not place. Curate via Size Aliases or
            Size Schemas → applicable families, then reclassify to apply.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleReclassify} disabled={reclassifying}>
            {reclassifying ? 'Reclassifying...' : 'Reclassify Now'}
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      {summary && (
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Card sx={{ minWidth: 180 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Unknown rows</Typography>
              <Typography variant="h4">{summary.unknown_count.toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary">{summary.unknown_percent}% of total</Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 180 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Classified rows</Typography>
              <Typography variant="h4">{summary.classified_count.toLocaleString()}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 180 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Total sizes</Typography>
              <Typography variant="h4">{summary.total_count.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Retailer</InputLabel>
          <Select
            value={filters.retailer_id}
            label="Retailer"
            onChange={(e) => setFilters({ ...filters, retailer_id: e.target.value })}
          >
            <MenuItem value=""><em>All</em></MenuItem>
            {filterOptions.retailers.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Family</InputLabel>
          <Select
            value={filters.family}
            label="Family"
            onChange={(e) => setFilters({ ...filters, family: e.target.value })}
          >
            <MenuItem value=""><em>All</em></MenuItem>
            {filterOptions.families.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Raw size contains..."
          value={filters.raw_size_search}
          onChange={(e) => setFilters({ ...filters, raw_size_search: e.target.value })}
          sx={{ minWidth: 200 }}
        />
        {(filters.retailer_id || filters.family || filters.raw_size_search) && (
          <Button size="small" onClick={() => setFilters({ retailer_id: '', family: '', raw_size_search: '' })}>
            Clear
          </Button>
        )}
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Retailer</strong></TableCell>
              <TableCell><strong>Family</strong></TableCell>
              <TableCell><strong>Normalized Category</strong></TableCell>
              <TableCell><strong>Raw Size</strong></TableCell>
              <TableCell align="right"><strong>Count</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g, i) => (
              <TableRow key={`${g.retailer_id}-${g.raw_size}-${i}`}>
                <TableCell><Typography variant="body2">{g.retailer_id}</Typography></TableCell>
                <TableCell>{g.category_family ? <Chip label={g.category_family} size="small" /> : <Typography variant="caption" color="text.secondary">—</Typography>}</TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{g.normalized_category || '—'}</Typography></TableCell>
                <TableCell><Chip label={g.raw_size} size="small" variant="outlined" /></TableCell>
                <TableCell align="right"><Typography variant="body2"><strong>{g.row_count.toLocaleString()}</strong></Typography></TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<SearchIcon />} onClick={() => handleDrilldown(g)}>Inspect</Button>
                </TableCell>
              </TableRow>
            ))}
            {groups.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No unknown sizes — curation queue is clear.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={drilldownOpen} onClose={() => setDrilldownOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {drilldown.retailer_id} / <code>{drilldown.raw_size}</code>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">In Stock</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drilldown.rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Typography variant="body2">{r.raw_product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.raw_product_id}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{r.normalized_category || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{r.normalization_status}</Typography></TableCell>
                    <TableCell align="center">{r.in_stock_overall ? '✓' : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrilldownOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
